/**
 * Reactivesearch-compat: Vue 3 replacements for @appbaseio/reactivesearch-vue components.
 * Queries Elasticsearch directly via fetch with basic auth.
 *
 * Components: ReactiveBase, DataSearch, MultiList, ReactiveList
 */
import { h, ref, reactive, computed, watch, provide, inject, onMounted, onBeforeUnmount, defineComponent } from 'vue'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function parseUrl(raw) {
  try {
    const u = new URL(raw)
    const auth = u.username && u.password
      ? 'Basic ' + btoa(decodeURIComponent(u.username) + ':' + decodeURIComponent(u.password))
      : null
    u.username = ''
    u.password = ''
    return { base: u.origin + u.pathname.replace(/\/+$/, ''), auth }
  } catch {
    return { base: raw, auth: null }
  }
}

async function esSearch(base, auth, index, body) {
  // Searchly requires _msearch endpoint (not _search) with NDJSON format.
  // _msearch body: header line (empty obj for index) + newline + query line + newline
  const url = `${base}/${index}/_msearch`
  const headers = {
    'Content-Type': 'application/x-ndjson',
    'Accept': 'application/json',
  }
  if (auth) headers['Authorization'] = auth

  // _msearch format: each request is 2 lines (header + body), terminated by newline.
  //
  // ignore_unavailable, because the index here is often a list of every
  // index a result can live in, and one of those not existing yet is
  // ordinary — a search type that has never been used has no index. The
  // default is to fail the whole request for a missing index, which
  // turns "no trees yet" into an empty History for everything else too.
  const ndjson = JSON.stringify({ ignore_unavailable: true }) + '\n' + JSON.stringify(body) + '\n'

  const resp = await fetch(url, { method: 'POST', headers, body: ndjson })
  if (!resp.ok) throw new Error(`ES ${resp.status}: ${resp.statusText}`)
  const result = await resp.json()
  // _msearch returns { responses: [...] } — unwrap first response
  if (result.responses && result.responses.length > 0) {
    const first = result.responses[0]
    // A per-response error is reported in the body with a 200 on the
    // envelope, so it has to be raised here or it reads as zero hits.
    if (first && first.error) {
      const type = first.error.type || 'search failed'
      throw new Error(`ES ${first.status || ''}: ${type}`.trim())
    }
    return first
  }
  return result
}

/**
 * One search against an index (or comma-separated list of indices),
 * with a caller-built body — for a component that wants rows outside
 * the reactive-list's own page, such as a full result set to page
 * through elsewhere. Same endpoint and auth as the components use.
 */
export async function esSearchIndex(rawUrl, index, body) {
  const { base, auth } = parseUrl(rawUrl)
  return esSearch(base, auth, index, body)
}

/**
 * Count matches for several (index, query) pairs in one round trip.
 *
 * One `_msearch` with a header/body pair per entry; each body is a
 * `size: 0` search with exact totals. Missing indices count as zero
 * rather than failing the batch, for the same reason as `esSearch`.
 * Returns an array of numbers in the order given (null where that one
 * request errored).
 */
export async function esMultiCount(rawUrl, requests) {
  const { base, auth } = parseUrl(rawUrl)
  if (!requests || !requests.length) return []
  const headers = {
    'Content-Type': 'application/x-ndjson',
    'Accept': 'application/json',
  }
  if (auth) headers['Authorization'] = auth

  // The read-only credential is scoped by index PATH, and the proxy
  // refuses a bare `/_msearch` and any per-request header that names
  // an index (both 401). So: one `_msearch` per index, addressed on
  // the path with empty headers — the exact shape `esSearch` uses —
  // and the batches run side by side.
  const byIndex = new Map()
  requests.forEach((r, i) => {
    if (!byIndex.has(r.index)) byIndex.set(r.index, [])
    byIndex.get(r.index).push(i)
  })
  const out = new Array(requests.length).fill(null)
  await Promise.all([...byIndex.entries()].map(async ([index, positions]) => {
    const ndjson = positions.map(i =>
      JSON.stringify({ ignore_unavailable: true }) + '\n' +
      JSON.stringify({ size: 0, track_total_hits: true, query: requests[i].query }) + '\n'
    ).join('')
    try {
      const resp = await fetch(`${base}/${index}/_msearch`, { method: 'POST', headers, body: ndjson })
      if (!resp.ok) throw new Error(`ES ${resp.status}: ${resp.statusText}`)
      const result = await resp.json()
      const responses = (result && result.responses) || []
      positions.forEach((pos, j) => {
        const r = responses[j]
        if (!r || r.error || !r.hits) return
        const t = r.hits.total
        out[pos] = typeof t === 'object' ? t.value : t
      })
    } catch (err) {
      console.warn(`[esMultiCount ${index}]`, err)
    }
  }))
  return out
}

// Combine queries from registered components based on the react prop.
function buildCombinedQuery(react, registry, selfId) {
  const must = []
  const should = []

  const addIds = (ids, target) => {
    if (!ids) return
    const arr = Array.isArray(ids) ? ids : [ids]
    arr.forEach(id => {
      if (id === selfId) return
      const entry = registry[id]
      if (entry && entry.query) target.push(entry.query)
    })
  }

  if (react) {
    addIds(react.and, must)
    addIds(react.or, should)
  }
  if (must.length === 0 && should.length === 0) return null
  const bool = {}
  if (must.length) bool.must = must
  if (should.length) { bool.should = should; bool.minimum_should_match = 1 }
  return { bool }
}

// ─── Symbols ──────────────────────────────────────────────────────────────────

const RS_KEY = Symbol('reactivesearch')

// ─── ReactiveBase ─────────────────────────────────────────────────────────────

const ReactiveBase = defineComponent({
  name: 'ReactiveBase',
  props: {
    app: { type: String, required: true },
    url: { type: String, required: true },
  },
  setup(props, { slots }) {
    const registry = reactive({})       // componentId -> { query, type }
    const listeners = reactive({})      // componentId -> callback
    const parsed = computed(() => parseUrl(props.url))

    function register(componentId, type) {
      registry[componentId] = { query: null, type }
    }

    function setQuery(componentId, query) {
      if (registry[componentId]) {
        registry[componentId] = { ...registry[componentId], query }
      }
      // Notify all listeners
      Object.keys(listeners).forEach(id => {
        if (id !== componentId && listeners[id]) listeners[id](componentId)
      })
    }

    function subscribe(componentId, cb) {
      listeners[componentId] = cb
    }

    function unsubscribe(componentId) {
      delete listeners[componentId]
    }

    function unregister(componentId) {
      delete registry[componentId]
      delete listeners[componentId]
    }

    provide(RS_KEY, {
      app: computed(() => props.app),
      base: computed(() => parsed.value.base),
      auth: computed(() => parsed.value.auth),
      registry,
      register,
      setQuery,
      subscribe,
      unsubscribe,
      unregister,
    })

    return () => h('div', { class: 'reactive-base' }, slots.default ? slots.default() : [])
  }
})

// ─── DataSearch ───────────────────────────────────────────────────────────────

const DataSearch = defineComponent({
  name: 'DataSearch',
  props: {
    componentId: { type: String, required: true },
    dataField: { type: [String, Array], default: 'name' },
    placeholder: { type: String, default: 'Search...' },
    fuzziness: { type: String, default: 'AUTO' },
    debounce: { type: Number, default: 300 },
    autosuggest: { type: Boolean, default: false },
    highlight: { type: Boolean, default: false },
    title: { type: String, default: '' },
    queryFormat: { type: String, default: 'or' },
    autocompleteSuggestions: { type: Boolean, default: false },
  },
  emits: ['valueChange'],
  setup(props, { emit }) {
    const rs = inject(RS_KEY)
    const searchText = ref('')
    let timer = null

    rs.register(props.componentId, 'search')

    function buildQuery(val) {
      if (!val || !val.trim()) return null
      const fields = Array.isArray(props.dataField) ? props.dataField : [props.dataField]
      return {
        multi_match: {
          query: val,
          fields,
          fuzziness: props.fuzziness || 'AUTO',
          type: 'best_fields',
          operator: props.queryFormat === 'and' ? 'and' : 'or',
        }
      }
    }

    function onInput(e) {
      const val = e.target.value
      searchText.value = val
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => {
        const q = buildQuery(val)
        rs.setQuery(props.componentId, q)
        emit('valueChange', val)
      }, props.debounce)
    }

    onBeforeUnmount(() => {
      if (timer) clearTimeout(timer)
      rs.unregister(props.componentId)
    })

    return () => {
      const children = []
      if (props.title) {
        children.push(h('label', { class: 'data-search-title' }, props.title))
      }
      children.push(h('input', {
        type: 'text',
        class: 'data-search-input',
        placeholder: props.placeholder,
        value: searchText.value,
        onInput,
      }))
      return h('div', { class: 'data-search' }, children)
    }
  }
})

// ─── MultiList ────────────────────────────────────────────────────────────────

const MultiList = defineComponent({
  name: 'MultiList',
  props: {
    componentId: { type: String, required: true },
    dataField: { type: String, required: true },
    title: { type: String, default: '' },
    showSearch: { type: Boolean, default: false },
    defaultQuery: { type: Function, default: null },
    react: { type: Object, default: null },
  },
  setup(props, { slots }) {
    const rs = inject(RS_KEY)
    const buckets = ref([])
    const selected = reactive(new Set())
    const filterText = ref('')
    let fetchTimer = null

    rs.register(props.componentId, 'list')

    async function fetchAggs() {
      const body = { size: 0, aggs: { field_agg: { terms: { field: props.dataField, size: 200 } } } }

      // Apply defaultQuery
      if (props.defaultQuery) {
        const dq = props.defaultQuery()
        if (dq && dq.query) body.query = dq.query
      }

      // Apply react dependencies
      if (props.react) {
        const combined = buildCombinedQuery(props.react, rs.registry, props.componentId)
        if (combined) {
          if (body.query) {
            body.query = { bool: { must: [body.query, combined] } }
          } else {
            body.query = combined
          }
        }
      }

      try {
        const resp = await esSearch(rs.base.value, rs.auth.value, rs.app.value, body)
        if (resp.aggregations && resp.aggregations.field_agg) {
          buckets.value = resp.aggregations.field_agg.buckets || []
        }
      } catch (err) {
        console.error(`[MultiList ${props.componentId}] fetch error:`, err)
      }
    }

    function toggleItem(key) {
      if (selected.has(key)) selected.delete(key)
      else selected.add(key)
      publishQuery()
    }

    function publishQuery() {
      if (selected.size === 0) {
        rs.setQuery(props.componentId, null)
        return
      }
      const q = { terms: { [props.dataField]: [...selected] } }
      rs.setQuery(props.componentId, q)
    }

    // React to sibling changes
    rs.subscribe(props.componentId, () => {
      if (fetchTimer) clearTimeout(fetchTimer)
      fetchTimer = setTimeout(fetchAggs, 100)
    })

    // Watch app changes (key changes cause remount, but just in case)
    watch(() => rs.app.value, () => {
      selected.clear()
      fetchAggs()
    })

    onMounted(() => fetchAggs())

    onBeforeUnmount(() => {
      if (fetchTimer) clearTimeout(fetchTimer)
      rs.unregister(props.componentId)
    })

    const filteredBuckets = computed(() => {
      if (!filterText.value) return buckets.value
      const lc = filterText.value.toLowerCase()
      return buckets.value.filter(b => b.key.toLowerCase().includes(lc))
    })

    return () => {
      const children = []
      if (props.title) {
        children.push(h('div', { class: 'multi-list-title' }, props.title))
      }
      if (props.showSearch) {
        children.push(h('input', {
          type: 'text',
          class: 'multi-list-search',
          placeholder: 'Filter...',
          value: filterText.value,
          onInput: e => { filterText.value = e.target.value },
        }))
      }

      const items = filteredBuckets.value.map(b => {
        const isChecked = selected.has(b.key)
        const label = b.key
        const count = b.doc_count

        // Use scoped slot if provided
        const content = slots.renderItem
          ? slots.renderItem({ label, count })
          : h('span', null, `${label} (${count})`)

        return h('label', { class: 'multi-list-item', key: label }, [
          h('input', {
            type: 'checkbox',
            checked: isChecked,
            onChange: () => toggleItem(b.key),
          }),
          content,
        ])
      })

      children.push(h('div', { class: 'multi-list-items' }, items))
      return h('div', { class: 'multi-list' }, children)
    }
  }
})

// ─── ReactiveList ─────────────────────────────────────────────────────────────

const ReactiveList = defineComponent({
  name: 'ReactiveList',
  props: {
    componentId: { type: String, required: true },
    dataField: { type: String, default: 'date' },
    pagination: { type: Boolean, default: true },
    sortBy: { type: String, default: 'desc' },
    from: { type: Number, default: 0 },
    size: { type: Number, default: 10 },
    defaultQuery: { type: Function, default: null },
    react: { type: Object, default: null },
    showResultStats: { type: Boolean, default: false },
    innerClass: { type: Object, default: () => ({}) },
    renderResultStats: { type: Function, default: null },
    debug: { type: Boolean, default: false },
  },
  emits: ['queryChange', 'pageChange', 'data'],
  setup(props, { slots, emit, expose }) {
    const rs = inject(RS_KEY)
    const data = ref([])
    const total = ref(0)
    const page = ref(1)
    const took = ref(0)
    const loading = ref(false)
    let fetchTimer = null
    let lastQueryJson = ''

    rs.register(props.componentId, 'result')

    function setPage(n) {
      if (n < 1) n = 1
      const maxPage = Math.max(1, Math.ceil(total.value / props.size))
      if (n > maxPage) n = maxPage
      if (page.value === n && data.value.length > 0) return
      page.value = n
      fetchResults()
      emit('pageChange', n)
    }

    function setTotal(n) {
      total.value = n
    }

    expose({ setPage, setTotal })

    async function fetchResults() {
      const body = {
        from: (page.value - 1) * props.size,
        size: props.size,
      }

      // Sort
      if (props.dataField) {
        body.sort = [{ [props.dataField]: { order: props.sortBy || 'desc' } }]
      }

      // Build query
      const must = []

      // defaultQuery
      if (props.defaultQuery) {
        const dq = props.defaultQuery()
        if (dq && dq.query) must.push(dq.query)
      }

      // react dependencies
      if (props.react) {
        const combined = buildCombinedQuery(props.react, rs.registry, props.componentId)
        if (combined) must.push(combined)
      }

      if (must.length === 1) {
        body.query = must[0]
      } else if (must.length > 1) {
        body.query = { bool: { must } }
      }

      const queryJson = JSON.stringify(body)
      if (queryJson !== lastQueryJson) {
        emit('queryChange', body)
        lastQueryJson = queryJson
      }

      loading.value = true
      try {
        const resp = await esSearch(rs.base.value, rs.auth.value, rs.app.value, body)
        took.value = resp.took || 0
        total.value = resp.hits ? (typeof resp.hits.total === 'object' ? resp.hits.total.value : resp.hits.total) : 0
        const hits = resp.hits ? resp.hits.hits : []
        // `_index` rides along because `app` may name several indices
        // at once, and anything that acts on a single row (deleting
        // it, say) has to address the index that row actually lives in.
        data.value = hits.map(hit => ({ ...hit._source, _id: hit._id, _score: hit._score, _index: hit._index }))
        emit('data', { data: data.value, resultStats: { numberOfResults: total.value, time: took.value, currentPage: page.value } })
      } catch (err) {
        console.error(`[ReactiveList ${props.componentId}] fetch error:`, err)
        data.value = []
        total.value = 0
        emit('data', { data: data.value, resultStats: { numberOfResults: 0, time: 0, currentPage: page.value } })
      } finally {
        loading.value = false
      }
    }

    // React to sibling changes
    rs.subscribe(props.componentId, () => {
      if (fetchTimer) clearTimeout(fetchTimer)
      fetchTimer = setTimeout(() => {
        page.value = 1
        fetchResults()
      }, 150)
    })

    watch(() => rs.app.value, () => {
      page.value = 1
      fetchResults()
    })

    onMounted(() => fetchResults())

    onBeforeUnmount(() => {
      if (fetchTimer) clearTimeout(fetchTimer)
      rs.unregister(props.componentId)
    })

    return () => {
      const paginationClass = props.innerClass.pagination || 'result-pagination'
      const noResultsClass = props.innerClass.noResults || 'no-results'
      const children = []

      // Result stats
      if (props.showResultStats && props.renderResultStats) {
        const statsText = props.renderResultStats({
          numberOfResults: total.value,
          time: took.value,
          currentPage: page.value,
        })
        children.push(h('div', { class: 'result-stats' }, statsText))
      }

      // Main content via scoped slot
      if (slots.render) {
        children.push(slots.render({ data: data.value, loading: loading.value }))
      } else if (data.value.length === 0 && !loading.value) {
        children.push(h('div', { class: noResultsClass }, 'No results found'))
      }

      // Pagination
      if (props.pagination && total.value > 0) {
        const totalPages = Math.ceil(total.value / props.size)
        if (totalPages > 1) {
          const pageButtons = []
          const cur = page.value
          const maxButtons = 7
          let startPage = Math.max(1, cur - Math.floor(maxButtons / 2))
          let endPage = Math.min(totalPages, startPage + maxButtons - 1)
          if (endPage - startPage < maxButtons - 1) {
            startPage = Math.max(1, endPage - maxButtons + 1)
          }

          // Prev
          pageButtons.push(h('button', {
            class: 'page-btn prev',
            disabled: cur <= 1,
            onClick: () => setPage(cur - 1),
          }, '\u2039'))

          if (startPage > 1) {
            pageButtons.push(h('button', { class: 'page-btn', onClick: () => setPage(1) }, '1'))
            if (startPage > 2) pageButtons.push(h('span', { class: 'page-ellipsis' }, '...'))
          }

          for (let i = startPage; i <= endPage; i++) {
            pageButtons.push(h('button', {
              class: ['page-btn', i === cur ? 'active' : ''],
              onClick: () => setPage(i),
            }, String(i)))
          }

          if (endPage < totalPages) {
            if (endPage < totalPages - 1) pageButtons.push(h('span', { class: 'page-ellipsis' }, '...'))
            pageButtons.push(h('button', { class: 'page-btn', onClick: () => setPage(totalPages) }, String(totalPages)))
          }

          // Next
          pageButtons.push(h('button', {
            class: 'page-btn next',
            disabled: cur >= totalPages,
            onClick: () => setPage(cur + 1),
          }, '\u203A'))

          children.push(h('div', { class: paginationClass }, pageButtons))
        }
      }

      return h('div', { class: 'reactive-list' }, children)
    }
  }
})

// ─── Plugin ───────────────────────────────────────────────────────────────────

export default {
  install(app) {
    app.component('reactive-base', ReactiveBase)
    app.component('data-search', DataSearch)
    app.component('multi-list', MultiList)
    app.component('reactive-list', ReactiveList)
  }
}

export { ReactiveBase, DataSearch, MultiList, ReactiveList }
